import type { ILocationResult } from '@components/searchCity';
import type { LeagueEvent } from '@interfaces/APITravel';
import {
  getCityLatLonByName,
  type GeocodeContext,
} from '@v2/services/getCityLatLonByName';
import { getLatLng, searchLocation } from '@utils/location';
import { isMapCoordinate } from './hotel-presentation';

export const HOTEL_SEARCH_AREAS = ['stadium', 'downtown', 'custom'] as const;
export type HotelSearchArea = (typeof HOTEL_SEARCH_AREAS)[number];

export const GEOCODE_RETRY_ATTEMPTS = 3;
export const GEOCODE_RETRY_DELAY_MS = 300;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const DOWNTOWN_CACHE_KEY = 'hotel_downtown_location_cache_v2';
const AIRPORT_PIN_CACHE_KEY = 'hotel_airport_pin_cache';

export type HotelMapPin = {
  latitude: number;
  longitude: number;
  label: string;
};

export type HotelAreaCommit = {
  area: HotelSearchArea;
  location: ILocationResult;
};

type DowntownLocationQuery = GeocodeContext & {
  city: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isLocationResult(value: unknown): value is ILocationResult {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.latitude === 'number' &&
    typeof value.longitude === 'number' &&
    isMapCoordinate(value.latitude, value.longitude)
  );
}

function isMapPin(value: unknown): value is HotelMapPin {
  if (!isRecord(value)) return false;
  return (
    typeof value.latitude === 'number' &&
    typeof value.longitude === 'number' &&
    typeof value.label === 'string' &&
    isMapCoordinate(value.latitude, value.longitude)
  );
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function retryUntilDefined<T>(
  load: () => Promise<T | null>,
  {
    attempts = GEOCODE_RETRY_ATTEMPTS,
    delayMs = GEOCODE_RETRY_DELAY_MS,
  }: { attempts?: number; delayMs?: number } = {}
): Promise<T | null> {
  let last: T | null = null;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      last = await load();
      if (last) return last;
    } catch {
      last = null;
    }
    if (attempt < attempts - 1 && delayMs > 0) {
      await sleep(delayMs * (attempt + 1));
    }
  }
  return last;
}

function readCache(key: string): Record<string, unknown> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return {};
    return parsed;
  } catch {
    return {};
  }
}

function writeCache(key: string, value: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function cacheTimestamp(entry: unknown): number | null {
  if (!isRecord(entry) || typeof entry.timestamp !== 'number') return null;
  return entry.timestamp;
}

export function isHotelSearchArea(value: unknown): value is HotelSearchArea {
  return value === 'stadium' || value === 'downtown' || value === 'custom';
}

export function coerceHotelSearchArea(value: unknown): HotelSearchArea {
  return isHotelSearchArea(value) ? value : 'stadium';
}

export function venueCityName(event?: LeagueEvent | null): string {
  const venue = event?.venue;
  if (!venue) return '';
  const locality = venue.address?.locality?.trim();
  if (locality) return locality;
  const location = venue.location?.trim();
  if (!location) return '';
  return location.split(',')[0]?.trim() ?? '';
}

export function venueDisplayCity(event?: LeagueEvent | null): string {
  const venue = event?.venue;
  if (!venue) return '';
  return (
    venue.address?.locality?.trim() ||
    venue.location?.trim() ||
    venue.address?.location?.trim() ||
    ''
  );
}

export function stadiumLocationFromEvent(
  event?: LeagueEvent | null
): ILocationResult | null {
  const venue = event?.venue;
  if (!venue) return null;
  const latitude = venue.address?.latitude;
  const longitude = venue.address?.longitude;
  if (
    latitude == null ||
    longitude == null ||
    !isMapCoordinate(latitude, longitude)
  ) {
    return null;
  }
  return {
    id: String(venue.id),
    name: venue.name,
    country_name: venue.address?.country_code ?? '',
    city_name: venue.address?.location || venueCityName(event),
    latitude,
    longitude,
  };
}

export function hotelAreaChipLabel({
  area,
  venueName,
  city,
  customName,
}: {
  area: HotelSearchArea;
  venueName?: string;
  city?: string;
  customName?: string;
}): string {
  if (area === 'downtown') {
    return city ? `Near Downtown ${city}` : 'Near Downtown';
  }
  if (area === 'custom' && customName) return customName;
  if (venueName && city) return `Near ${venueName} - ${city}`;
  if (venueName) return `Near ${venueName}`;
  return city ? `Near ${city}` : 'Choose location';
}

function downtownCacheKey({ city, region, country }: DowntownLocationQuery) {
  const normalizedCity = city.trim().toLowerCase();
  if (!normalizedCity) return '';
  return JSON.stringify([
    normalizedCity,
    region?.trim().toLowerCase() ?? '',
    country?.trim().toLowerCase() ?? '',
  ]);
}

export function getCachedDowntownLocation(
  query: DowntownLocationQuery
): ILocationResult | null | undefined {
  const key = downtownCacheKey(query);
  if (!key) return undefined;
  const cache = readCache(DOWNTOWN_CACHE_KEY);
  const entry = cache[key];
  const timestamp = cacheTimestamp(entry);
  if (!timestamp) return undefined;
  if (Date.now() - timestamp > CACHE_TTL_MS) {
    delete cache[key];
    writeCache(DOWNTOWN_CACHE_KEY, cache);
    return undefined;
  }
  const location = isRecord(entry) ? entry.location : undefined;
  return isLocationResult(location) ? location : undefined;
}

function setCachedDowntownLocation(
  query: DowntownLocationQuery,
  location: ILocationResult
) {
  const key = downtownCacheKey(query);
  if (!key) return;
  const cache = readCache(DOWNTOWN_CACHE_KEY);
  cache[key] = { location, timestamp: Date.now() };
  writeCache(DOWNTOWN_CACHE_KEY, cache);
}

function toDowntownLocation(
  city: string,
  country: string,
  result: { Geometry?: { lat?: number; lon?: number }; Label?: string }
): ILocationResult | null {
  const latitude = result.Geometry?.lat;
  const longitude = result.Geometry?.lon;
  if (
    latitude == null ||
    longitude == null ||
    !isMapCoordinate(latitude, longitude)
  ) {
    return null;
  }
  return {
    id: `downtown:${city}`,
    name: `Downtown ${city}`,
    city_name: city,
    country_name: country,
    latitude,
    longitude,
  };
}

export async function resolveDowntownLocation({
  city,
  region = '',
  country = '',
  retry,
}: {
  city: string;
  region?: string;
  country?: string;
  retry?: { attempts?: number; delayMs?: number };
}): Promise<ILocationResult | null> {
  const trimmed = city.trim();
  if (!trimmed) return null;
  const context = { region: region.trim(), country: country.trim() };
  const query = { city: trimmed, ...context };
  const cached = getCachedDowntownLocation(query);
  if (cached) return cached;
  const result = await retryUntilDefined(
    () => getCityLatLonByName(trimmed, context),
    retry
  );
  if (!result) return null;
  const location = toDowntownLocation(trimmed, context.country, result);
  if (!location) return null;
  setCachedDowntownLocation(query, location);
  return location;
}

function airportCacheKey(code: string) {
  return code.trim().toUpperCase();
}

export function getCachedAirportPin(
  code: string
): HotelMapPin | null | undefined {
  const key = airportCacheKey(code);
  if (!key) return undefined;
  const cache = readCache(AIRPORT_PIN_CACHE_KEY);
  const entry = cache[key];
  const timestamp = cacheTimestamp(entry);
  if (!timestamp) return undefined;
  if (Date.now() - timestamp > CACHE_TTL_MS) {
    delete cache[key];
    writeCache(AIRPORT_PIN_CACHE_KEY, cache);
    return undefined;
  }
  const pin = isRecord(entry) ? entry.pin : undefined;
  return isMapPin(pin) ? pin : undefined;
}

function setCachedAirportPin(code: string, pin: HotelMapPin) {
  const key = airportCacheKey(code);
  if (!key) return;
  const cache = readCache(AIRPORT_PIN_CACHE_KEY);
  cache[key] = { pin, timestamp: Date.now() };
  writeCache(AIRPORT_PIN_CACHE_KEY, cache);
}

function pointToPin(point: unknown, label: string): HotelMapPin | null {
  if (!Array.isArray(point) || point.length < 2) return null;
  const longitude = Number(point[0]);
  const latitude = Number(point[1]);
  if (!isMapCoordinate(latitude, longitude)) return null;
  return { latitude, longitude, label };
}

export async function resolveAirportPin({
  airportCode,
  airportName,
  city,
  retry,
}: {
  airportCode: string;
  airportName?: string;
  city?: string;
  retry?: { attempts?: number; delayMs?: number };
}): Promise<HotelMapPin | null> {
  const code = airportCode.trim();
  if (!code) return null;
  const cached = getCachedAirportPin(code);
  if (cached) return cached;

  const query = [airportName, city, code].filter(Boolean).join(', ');
  const pin = await retryUntilDefined(async () => {
    const suggestions = await searchLocation(query);
    const match =
      Array.isArray(suggestions) &&
      suggestions.find(
        (item: { PlaceId?: string; Text?: string }) => item?.PlaceId
      );
    if (!match?.PlaceId) return null;
    const place = await getLatLng(match.PlaceId);
    return pointToPin(place?.Geometry?.Point, 'Airport');
  }, retry);

  if (!pin) return null;
  setCachedAirportPin(code, pin);
  return pin;
}

export function locationForArea({
  area,
  stadium,
  downtown,
  custom,
}: {
  area: HotelSearchArea;
  stadium: ILocationResult | null;
  downtown: ILocationResult | null;
  custom: ILocationResult | null;
}): ILocationResult | null {
  if (area === 'downtown') return downtown;
  if (area === 'custom') return custom;
  return stadium;
}
